import { z } from 'zod';
import { macroSetSchema } from './food.js';
import { daySummarySchema, mealEntryViewSchema } from './log.js';
import { mealSlotSchema } from './planning.js';
/* ==========================================================================
 *  Unified day feed contract (ADR-0032 / T27.1 — mobile revamp)
 *
 *  ONE request (`GET /me/days/:date/feed`) returns everything the Today
 *  timeline renders: logged entries, weigh-ins, planned items, at most one
 *  nudge, and rhythm slots — time-sorted, plus totals-vs-targets and the
 *  day-closed/recap flags. The route COMPOSES existing queries
 *  (computeDaySummary, the ADR-0013 §10.1 entry list, meal-plan items); it
 *  never re-derives totals (FR-5.4).
 *
 *  The offline log queue (apps/mobile/src/db/log-queue.ts) stays the write
 *  side, untouched. Its rows become CLIENT-ONLY timeline members
 *  (`pendingLog` / `failedLog`) via `mergeDayFeedTimeline` below — the merge
 *  rule is committed here so T29.2/T29.3 render one list instead of
 *  re-deriving the dedup/ordering logic.
 *
 *  i18n invariant: the server sends numbers, enums, and user-authored content
 *  only — never display strings. All copy is templated client-side through
 *  t() (brand-guide §2 lexicon applies). ADR-0033 resolved the flagged nudge
 *  exception WITHOUT breaking the invariant: the feed carries sentence PARTS
 *  (kind + numbers); only the push mirror is server-rendered (push status quo).
 *
 *  Forward-compat (ADR-0032 §5): `nudge` and `slot` members exist in the
 *  union from day one; the feed emits none until Tracks 31/32 land, so the
 *  mobile contract never changes shape mid-pack.
 * ========================================================================== */
/* ----- Server-emitted timeline items ------------------------------------- */
/** A logged meal entry on the timeline. Wraps the existing entry view
 *  wholesale so the edit sheet (T29.4) works from the feed alone. */
export const dayFeedEntryItemSchema = z.object({
    type: z.literal('entry'),
    /** meal_entry id (duplicates entry.id — hoisted for uniform keying/sorting). */
    id: z.string().uuid(),
    /** Sort key = entry.recordedAt. Every union member carries `at`. */
    at: z.string().datetime(),
    /** Eyebrow slot label ("8:04 · BREAKFAST"). Server-derived from time-of-day
     *  bands (pure arithmetic, T28.1); T32.2 may refine with learned slots. */
    mealSlot: mealSlotSchema.nullable(),
    /** raw_log id this entry came from — the DEDUP KEY against the offline
     *  queue (queue row rawLogId ↔ this). Null for synthetic entries. */
    sourceLogId: z.string().uuid().nullable(),
    entry: mealEntryViewSchema,
    /** Portion factor applied via the edit sheet (design §5: "0.75× portion").
     *  Null until T29.4 persists it; client renders the note when ≠ 1. */
    portionMultiplier: z.number().positive().nullable().default(null),
    /** #ref delta provenance (design §2: "Chicken bowl · modified").
     *  Null until T30.1 fills it. `deltaText` is user-authored content. */
    refDiff: z
        .object({
        savedMealId: z.string().uuid(),
        savedMealName: z.string(),
        deltaText: z.string(),
    })
        .nullable()
        .default(null),
    /** User-authored macro-line suffix ("extra peanut butter"). Never a
     *  server-composed display string. */
    note: z.string().nullable().default(null),
});
/** A weigh-in rendered as a kcal-less timeline entry (handoff §2). */
export const dayFeedWeighInItemSchema = z.object({
    type: z.literal('weighIn'),
    id: z.string().uuid(),
    at: z.string().datetime(),
    weightKg: z.number().positive(),
});
/** A planned item ("~16:30 · PLANNED · +21 g protein"). Sourced from meal-plan
 *  items today; T31.1's coach-accepted proposals write into the same shape. */
export const dayFeedPlannedItemSchema = z.object({
    type: z.literal('planned'),
    /** meal_plan_item id — the id `POST /me/planned-items/:id/log` acts on. */
    id: z.string().uuid(),
    /** Nullable: untimed plans sort after the last timed item. */
    at: z.string().datetime().nullable(),
    label: z.string(),
    mealSlot: z.string().nullable(),
    savedMealId: z.string().uuid().nullable(),
    /** Expected contribution (drives "+21 g protein"). Null when unknown. */
    macros: macroSetSchema.nullable(),
});
/** Nudge rule set (ADR-0033 §1 + T48.1). Floor-recovery/readiness kinds ONLY — an
 *  over-target/restrictive kind has no value to take (ADR-0009 §4, structural
 *  ED-safety). `slot_bridge` lands with Track 32; the enum reserves it now. */
export const nudgeKindSchema = z.enum([
    'protein_gap',
    'fibre_gap',
    'slot_bridge',
    'coaching_ready',
]);
/** The nudge's one primary action (design §1: one sentence, one action, one
 *  dismiss). `ask_coach` opens a thread pre-loaded via GET /me/coach/context;
 *  `log_prefill` (slot_bridge, T32) drops prefillText into the composer. */
export const nudgeActionSchema = z.object({
    kind: z.enum(['ask_coach', 'log_prefill']),
    /** Natural-language composer prefill for `log_prefill`; null for `ask_coach`. */
    prefillText: z.string().nullable(),
});
/** Deterministic nudge identity (ADR-0033 §2): stable across derive-on-read
 *  recomputes, carried by the push mirror (`nudgeId` custom datum + WonderPush
 *  `alert.tag`), and the id POST /me/nudges/:id/dismiss acts on. */
export function nudgeIdFor(date, kind) {
    return `nudge:${date}:${kind}`;
}
/** The at-most-one nudge (body frozen by ADR-0033 / T27.2 — the one
 *  sanctioned edit to this envelope). Derived on read, never persisted; the
 *  only persisted state is the dismissal (notification_log.dismissed_at).
 *  Sentence PARTS only — the client templates the sentence via t() (number
 *  first, additive framing). The feed returns no nudge items until T31.1. */
export const dayFeedNudgeItemSchema = z.object({
    type: z.literal('nudge'),
    /** = nudgeIdFor(date, kind). */
    id: z.string(),
    /** Server-assigned anchor time — where in the timeline the nudge applies. */
    at: z.string().datetime().nullable(),
    kind: nudgeKindSchema,
    /** Sentence parts: which floor + how far from it (computeDaySummary.remaining,
     *  ADR-0009 §2 — no new arithmetic). Null for non-gap kinds. */
    macro: z.string().nullable(),
    gapG: z.number().nullable(),
    /** Anchor slot for `slot_bridge`; null for gap kinds. */
    mealSlot: z.string().nullable(),
    action: nudgeActionSchema,
    createdAt: z.string().datetime(),
    /** End of the user-local day — a nudge never carries over (ADR-0033 §3). */
    expiresAt: z.string().datetime(),
});
/** A learned rhythm slot ("Snack · usually ~16:30 · room for ~200 kcal").
 *  Fields frozen here; the learning algorithm is ADR-0035 (T32.1). Empty
 *  until T32.2 ships. Copy is client-templated — always an invitation. */
export const dayFeedSlotItemSchema = z.object({
    type: z.literal('slot'),
    /** Stable per-day synthetic id, e.g. "slot:snack". */
    id: z.string(),
    /** usualTime resolved onto the feed date — the sort key. */
    at: z.string().datetime(),
    mealSlot: mealSlotSchema,
    /** "HH:MM" user-local. */
    usualTime: z.string().regex(/^\d{2}:\d{2}$/),
    /** Remaining kcal budget for the slot (server arithmetic). Null = don't show. */
    roomKcal: z.number().int().nullable(),
});
/** What the SERVER may emit in `timeline`. Max one `nudge` member per feed —
 *  enforced and tested server-side (T28.1/T31.1); the client only renders. */
export const dayFeedServerItemSchema = z.discriminatedUnion('type', [
    dayFeedEntryItemSchema,
    dayFeedWeighInItemSchema,
    dayFeedPlannedItemSchema,
    dayFeedNudgeItemSchema,
    dayFeedSlotItemSchema,
]);
/* ----- Client-only timeline items (offline queue merge) ------------------ */
/** Render state for a not-yet-acked capture. T29.3 maps these onto the
 *  3-tier sync model: queued/syncing/retrying = tier 1 (quiet),
 *  needs_clarification = tier 2 (informative). */
export const pendingSyncStateSchema = z.enum([
    'queued',
    'syncing',
    'retrying',
    'needs_clarification',
]);
/** A queue row the server hasn't acked into the feed yet ("◌ syncing").
 *  NEVER emitted by the server — injected by `mergeDayFeedTimeline`. */
export const dayFeedPendingLogItemSchema = z.object({
    type: z.literal('pendingLog'),
    /** = clientLogId (the queue row's id and the server idempotency key). */
    id: z.string().uuid(),
    /** = clientNow (capture-time clock, ADR-0003 §M4). */
    at: z.string().datetime(),
    text: z.string(),
    /** Local file URI for photo captures; null for text logs. */
    imageUri: z.string().nullable(),
    sync: pendingSyncStateSchema,
});
/** A permanently failed capture — the tier-3 actionable card ("Fix text" /
 *  "Discard"). NEVER emitted by the server. `reason` is the queue's sentinel
 *  or error message (opaque; client maps sentinels to ED-safe copy). */
export const dayFeedFailedLogItemSchema = z.object({
    type: z.literal('failedLog'),
    id: z.string().uuid(),
    at: z.string().datetime(),
    text: z.string(),
    imageUri: z.string().nullable(),
    reason: z.string().nullable(),
});
/** The full render contract for the Today timeline: server items ∪ queue
 *  items. T29.2 renders exactly this list — one system, not two. */
export const dayFeedTimelineItemSchema = z.discriminatedUnion('type', [
    dayFeedEntryItemSchema,
    dayFeedWeighInItemSchema,
    dayFeedPlannedItemSchema,
    dayFeedNudgeItemSchema,
    dayFeedSlotItemSchema,
    dayFeedPendingLogItemSchema,
    dayFeedFailedLogItemSchema,
]);
/* ----- GET /me/days/:date/feed ------------------------------------------- */
/** `today` = the client's local calendar date, so `dayClosed` respects the
 *  user's clock (same trust model as clientNow/timezone, ADR-0003 §M4).
 *  Server defaults to its UTC today when absent. `clientNow` is the client's
 *  current clock (ADR-0035 §4 V6 — the rhythm-slot grace window); server
 *  defaults to its own clock when absent. */
export const dayFeedQuerySchema = z.object({
    today: z.string().date().optional(),
    clientNow: z.string().datetime().optional(),
});
export const dayFeedResponseSchema = z.object({
    date: z.string().date(),
    /** Totals-vs-targets — the unchanged ADR-0003 DaySummary, so the
     *  `response.day` echo from POST /me/logs writes straight into it. */
    totals: daySummarySchema,
    /** Server items, pre-sorted by the ordering rule below. */
    timeline: z.array(dayFeedServerItemSchema),
    /** date < today (user-local). Past days: no nudge, no slots (design §7). */
    dayClosed: z.boolean(),
    /** Recap pointer — fetch via GET /me/days/:date/recap (T28.2). */
    recapAvailable: z.boolean(),
    /** ADR-0033 I4's first-run definition, surfaced for the client (design §10
     *  first-run Today): no entries exist before this date. Gates the welcome
     *  card — the same single source that already withholds nudges/slots. */
    firstRun: z.boolean().default(false),
});
/* ----- GET /me/nudges/active, POST /me/nudges/:id/dismiss (ADR-0033/T31.1) */
export const activeNudgeResponseSchema = z.object({
    nudge: dayFeedNudgeItemSchema.nullable(),
});
/* ----- POST /me/planned-items, .../log, DELETE .../:id (ADR-0029/T31.1) --
 *
 * Coach-accepted proposals write into the same meal_plan_item rows the
 * planning layer already owns (ADR-0029 §3 — no parallel food model). */
export const createPlannedItemSchema = z
    .object({
    date: z.string().date(),
    mealSlot: z.string().min(1),
    savedMealId: z.string().uuid().optional(),
    recipeId: z.string().uuid().optional(),
    label: z.string().max(200).optional(),
    sortOrder: z.number().int().optional(),
})
    .refine((v) => v.savedMealId !== undefined || v.recipeId !== undefined || v.label !== undefined, {
    message: 'One of savedMealId, recipeId, or label is required.',
});
export const plannedItemResponseSchema = z.object({
    item: dayFeedPlannedItemSchema,
});
/** `POST /me/planned-items/:id/log` — the item's known macros write straight
 *  through the shared entry write path (no extraction parse); a saved
 *  meal/recipe with several ingredients yields one entry per ingredient. */
export const logPlannedItemResponseSchema = z.object({
    entries: z.array(dayFeedEntryItemSchema),
});
const PENDING_SYNC_BY_STATUS = {
    queued: 'queued',
    syncing: 'syncing',
    // Acked but the feed refetch hasn't landed yet — keep showing "syncing" so
    // the card never blinks out before its server entry appears.
    synced: 'syncing',
    needs_clarification: 'needs_clarification',
    failed: 'retrying',
};
/** Ordering rule: `at` ascending; null `at` sorts after the last timed item;
 *  ties break on `id` so the order is deterministic across refetches. */
export function compareDayFeedItems(a, b) {
    const ta = a.at === null ? Number.POSITIVE_INFINITY : Date.parse(a.at);
    const tb = b.at === null ? Number.POSITIVE_INFINITY : Date.parse(b.at);
    const na = Number.isNaN(ta) ? Number.POSITIVE_INFINITY : ta;
    const nb = Number.isNaN(tb) ? Number.POSITIVE_INFINITY : tb;
    if (na !== nb)
        return na - nb;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
/**
 * The client-side merge: server feed ∪ local queue → ONE time-sorted list.
 *
 * Rules (normative — ADR-0032 §3):
 *  - Only queue rows captured for this `date` (localDate) participate.
 *  - DEDUP: a row whose rawLogId matches an entry item's sourceLogId is
 *    dropped — the server entry has replaced it.
 *  - permanent_failed → `failedLog` (tier-3 card); everything else pending →
 *    `pendingLog` with the status map above.
 *  - Result is re-sorted with `compareDayFeedItems` (server order is already
 *    correct, but injected items must interleave by capture time).
 */
export function mergeDayFeedTimeline(server, queue, date) {
    const ackedLogIds = new Set();
    for (const item of server) {
        if (item.type === 'entry' && item.sourceLogId !== null)
            ackedLogIds.add(item.sourceLogId);
    }
    const injected = [];
    for (const row of queue) {
        if (row.localDate !== date)
            continue;
        if (row.rawLogId !== null && ackedLogIds.has(row.rawLogId))
            continue;
        if (row.status === 'permanent_failed') {
            injected.push({
                type: 'failedLog',
                id: row.clientLogId,
                at: row.clientNow,
                text: row.text,
                imageUri: row.imageUri ?? null,
                reason: row.errorMessage,
            });
        }
        else {
            injected.push({
                type: 'pendingLog',
                id: row.clientLogId,
                at: row.clientNow,
                text: row.text,
                imageUri: row.imageUri ?? null,
                sync: PENDING_SYNC_BY_STATUS[row.status],
            });
        }
    }
    return [...server, ...injected].sort(compareDayFeedItems);
}
//# sourceMappingURL=day-feed.js.map